import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useOrganizationList, useOrganization } from "@clerk/clerk-react";
import { useQueryClient } from "@tanstack/react-query";
import { registerActiveTenantSlug } from "@/lib/auth";

export interface TenantMembership {
  tenant: { slug: string; display_name: string; clerk_org_id: string };
  role: "admin" | "member";
}

interface TenantContextValue {
  activeSlug: string | null;
  memberships: TenantMembership[];
  setActiveBySlug: (slug: string) => Promise<void>;
}

const TenantContext = createContext<TenantContextValue | null>(null);

// Bumped from "bw_active_tenant_slug" to invalidate stale slug values from
// pre-v4.3.1 clients — we now store Clerk org IDs (stable) instead of slugs
// (Clerk auto-appends timestamps on conflict).
const ACTIVE_SLUG_KEY = "bw_active_tenant_id_v2";

/** Pulls Clerk organization list, syncs the active org to state, and mirrors
 *  the active slug to a module variable so apiFetch can attach X-BW-Tenant
 *  without going through React. */
export function TenantProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { organization } = useOrganization();
  const { userMemberships, setActive, isLoaded } = useOrganizationList({
    userMemberships: { infinite: true },
  });

  const [activeSlug, setActiveSlug] = useState<string | null>(() => {
    try {
      return localStorage.getItem(ACTIVE_SLUG_KEY);
    } catch {
      return null;
    }
  });

  // Derive memberships from Clerk's user-memberships list. We use the stable
  // Clerk org id (org_...) as the tenant identifier sent in X-BW-Tenant —
  // Clerk auto-mangles slugs on conflict (e.g. "saapavabrik-17764320..."),
  // so the slug isn't a reliable key. The backend accepts org ids and
  // resolves them against control.tenant_members.clerk_org_id.
  const memberships: TenantMembership[] = useMemo(() => {
    const list = userMemberships.data ?? [];
    return list.map((m) => ({
      tenant: {
        slug: m.organization.id,
        display_name: m.organization.name,
        clerk_org_id: m.organization.id,
      },
      role: m.role === "admin" || m.role === "org:admin" ? "admin" : "member",
    }));
  }, [userMemberships.data]);

  // Seed activeSlug from Clerk's current organization on first load / when
  // Clerk's state finally resolves.
  useEffect(() => {
    if (!isLoaded) return;
    if (!activeSlug && organization?.id) {
      setActiveSlug(organization.id);
    } else if (!activeSlug && memberships.length > 0) {
      setActiveSlug(memberships[0].tenant.slug);
    }
  }, [isLoaded, organization, memberships, activeSlug]);

  // Mirror the active slug to the module-level bridge so apiFetch picks it up.
  useEffect(() => {
    registerActiveTenantSlug(activeSlug);
    if (activeSlug) {
      try {
        localStorage.setItem(ACTIVE_SLUG_KEY, activeSlug);
      } catch {
        /* ignore */
      }
    }
  }, [activeSlug]);

  const setActiveBySlug = useCallback(
    async (slug: string) => {
      const match = memberships.find((m) => m.tenant.slug === slug);
      if (!match || !setActive) return;
      await setActive({ organization: match.tenant.clerk_org_id });
      setActiveSlug(slug);
      // Drop cross-tenant residue from the TanStack cache so the next fetch
      // lands under the new tenant.
      queryClient.clear();
    },
    [memberships, setActive, queryClient],
  );

  const value = useMemo<TenantContextValue>(
    () => ({ activeSlug, memberships, setActiveBySlug }),
    [activeSlug, memberships, setActiveBySlug],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error("useTenant must be used within <TenantProvider>");
  return ctx;
}
